/**
 * AIF-01 — "New model" dialog (§11.1). Captures name (required ≤100), type
 * (required select), description (optional multiline), POSTs via
 * `useCreateAiModelMutation`, and hands the created model back to the list.
 */
import { useState, type ReactElement } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import type { AiFindingModel, AiFindingModelType } from "@aeriosiq/types";
import { useCreateAiModelMutation } from "./aiModelQueries.js";
import { conflictMessage, errStatus } from "./apiError.js";
import { MODEL_TYPE_OPTIONS } from "./modelType.js";

export interface NewModelDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (created: AiFindingModel) => void;
}

export function NewModelDialog({
  open,
  onClose,
  onCreated,
}: NewModelDialogProps): ReactElement {
  const [name, setName] = useState("");
  const [type, setType] = useState<AiFindingModelType | "">("");
  const [description, setDescription] = useState("");
  const create = useCreateAiModelMutation();

  const nameValid = name.trim().length > 0 && name.trim().length <= 100;
  const typeSelected = type.length > 0;
  const canSubmit = nameValid && typeSelected && !create.isPending;

  function reset(): void {
    setName("");
    setType("");
    setDescription("");
    create.reset();
  }

  function handleClose(): void {
    reset();
    onClose();
  }

  function handleSubmit(): void {
    if (!canSubmit) return;
    const modelType = type;
    if (modelType === "") return;
    create.mutate(
      {
        modelName: name.trim(),
        modelType,
        description: description.trim().length > 0 ? description.trim() : null,
      },
      {
        onSuccess: (created) => {
          reset();
          onCreated(created);
        },
      },
    );
  }

  const errorMsg = create.isError
    ? errStatus(create.error) === 409
      ? conflictMessage(create.error, "Could not create the model.")
      : "Could not create the model. Check the details and your permissions."
    : null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>New AI model</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {errorMsg ? <Alert severity="error">{errorMsg}</Alert> : null}
          <TextField
            label="Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
            fullWidth
            required
            slotProps={{ htmlInput: { maxLength: 100 } }}
            error={name.length > 0 && !nameValid}
            helperText="Up to 100 characters."
          />
          <TextField
            select
            label="Type"
            value={type}
            onChange={(e) => {
              setType(e.target.value as AiFindingModelType);
            }}
            fullWidth
            required
          >
            {MODEL_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Description"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
            }}
            fullWidth
            multiline
            minRows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
